import { useCallback, useEffect } from "react";

interface UseAutoRowManagementProps<T> {
  rows: T[];
  addRow: () => void;
  isRowActive: (row: T) => boolean;
}

interface UseAutoRowManagementReturn {
  handleRowChange: () => void;
}

/**
 * Custom hook for automatic row management following SOLID principles
 * Single Responsibility: Manages automatic row addition logic
 * Open/Closed: Can be extended for different row types
 * Liskov Substitution: Works with any row type that has isRowActive check
 * Interface Segregation: Minimal interface for specific functionality
 * Dependency Inversion: Depends on abstractions, not concretions
 */
export const useAutoRowManagement = <T>({
  rows,
  addRow,
  isRowActive,
}: UseAutoRowManagementProps<T>): UseAutoRowManagementReturn => {
  const handleRowChange = useCallback(() => {
    // Check if we need to add a new row
    const shouldAddNewRow =
      rows.length === 0 ||
      (rows.length > 0 && isRowActive(rows[rows.length - 1]));

    if (shouldAddNewRow) {
      addRow();
    }
  }, [rows, addRow, isRowActive]);

  // Automatically add first row if none exist
  useEffect(() => {
    if (rows.length === 0) {
      addRow();
    }
  }, [rows.length, addRow]);

  // Monitor changes in the last row
  useEffect(() => {
    if (rows.length > 0) {
      const lastRow = rows[rows.length - 1];
      if (isRowActive(lastRow)) {
        // Check if this is the only row or if all other rows before it are also active
        const allPreviousRowsActive = rows
          .slice(0, -1)
          .every((row) => isRowActive(row));

        if (allPreviousRowsActive) {
          // Add a small delay to prevent rapid row creation
          const timeoutId = setTimeout(() => {
            addRow();
          }, 100);

          return () => clearTimeout(timeoutId);
        }
      }
    }
  }, [rows, addRow, isRowActive]);

  return {
    handleRowChange,
  };
};
